import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate, Link } from 'react-router-dom';
import type { AppDispatch, RootState } from '../../store';
import {
  fetchProposals,
  acceptProposal,
  declineProposal,
  counterProposal,
  setCurrentProposal,
} from '../../store/slices/gameSlice';
import { fetchMyTeams } from '../../store/slices/teamSlice';
import { fetchLeagueSeason } from '../../store/slices/leagueSlice';
import { GameProposalStatus } from '@sulis-liiga/common';
import SlotEditor, {
  emptySlot,
  isSlotComplete,
  isDateOutsideSeason,
  type SlotInput,
} from '../../components/game/SlotEditor';
import { respondingTeamId } from '../../utils/proposals';

const ProposalDetailPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { proposalId } = useParams<{ proposalId: string }>();
  const {
    proposals,
    currentProposal: proposal,
    loading,
    error,
  } = useSelector((state: RootState) => state.game);
  const { myTeams } = useSelector((state: RootState) => state.team);
  const { selectedLeagueId } = useSelector((state: RootState) => state.ui);
  const { currentSeason } = useSelector((state: RootState) => state.league);

  // The proposals list lives on the Games page's Proposals tab (the standalone
  // card page was removed). Back/return navigations go there.
  const proposalsHref = selectedLeagueId
    ? `/leagues/${selectedLeagueId}/games?tab=proposals`
    : '/games';

  const [showCounterForm, setShowCounterForm] = useState(false);
  const [counterSlots, setCounterSlots] = useState<SlotInput[]>([emptySlot()]);
  const [counterAttempted, setCounterAttempted] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    if (proposals.length === 0) {
      dispatch(fetchProposals(undefined));
    }
    // Load my teams so we can tell whether the viewer is the opponent (only the
    // opposing team may respond to a proposal).
    dispatch(fetchMyTeams());
  }, [dispatch, proposals.length]);

  useEffect(() => {
    if (proposalId && proposals.length > 0) {
      const found = proposals.find(p => p.id === proposalId);
      if (found) {
        dispatch(setCurrentProposal(found));
      }
    }
    return () => {
      dispatch(setCurrentProposal(null));
    };
  }, [dispatch, proposalId, proposals]);

  // Load the proposal's season so counter-proposal dates can be constrained to it.
  useEffect(() => {
    if (proposal?.seasonId && selectedLeagueId) {
      dispatch(fetchLeagueSeason({ leagueId: selectedLeagueId, seasonId: proposal.seasonId }));
    }
  }, [dispatch, proposal?.seasonId, selectedLeagueId]);

  const toISODate = (d: string | Date): string => new Date(d).toISOString().slice(0, 10);
  const seasonBounds =
    currentSeason && proposal && currentSeason.id === proposal.seasonId
      ? { minDate: toISODate(currentSeason.startDate), maxDate: toISODate(currentSeason.endDate) }
      : { minDate: undefined as string | undefined, maxDate: undefined as string | undefined };

  const handleAcceptSlot = async (slotIndex: number) => {
    if (!proposalId) return;
    setActionError(null);
    try {
      // Accepting schedules the game; land on the new scheduled game if the API
      // returned its id, otherwise fall back to the games list.
      const accepted = await dispatch(acceptProposal({ proposalId, slotIndex })).unwrap();
      navigate(accepted.scheduledGameId ? `/games/${accepted.scheduledGameId}` : '/games');
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    }
  };

  const handleDecline = async () => {
    if (!proposalId) return;
    setActionError(null);
    try {
      await dispatch(declineProposal({ proposalId })).unwrap();
      navigate(proposalsHref);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    }
  };

  const handleCounter = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!proposalId) return;
    setCounterAttempted(true);
    setActionError(null);
    const validSlots = counterSlots.filter(isSlotComplete);
    if (validSlots.length === 0) {
      setActionError(
        'Each time slot needs a date, time, venue and court/address. Complete the highlighted fields in at least one slot.',
      );
      return;
    }
    if (
      counterSlots.some(s =>
        isDateOutsideSeason(s.date, seasonBounds.minDate, seasonBounds.maxDate),
      )
    ) {
      setActionError(
        `Every proposed date must fall within the season (${seasonBounds.minDate} – ${seasonBounds.maxDate}). Fix the highlighted date.`,
      );
      return;
    }
    try {
      const slots = validSlots.map(s => ({
        date: s.date,
        time: s.time,
        venue: { name: s.venueName, address: s.venueAddress },
      }));
      await dispatch(counterProposal({ proposalId, slots })).unwrap();
      navigate(proposalsHref);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    }
  };

  const addCounterSlot = () => {
    setCounterSlots([...counterSlots, emptySlot()]);
  };

  const removeCounterSlot = (index: number) => {
    if (counterSlots.length > 1) {
      setCounterSlots(counterSlots.filter((_, i) => i !== index));
    }
  };

  const updateCounterSlot = (index: number, slot: SlotInput) => {
    const updated = [...counterSlots];
    updated[index] = slot;
    setCounterSlots(updated);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <svg
          className="animate-spin h-10 w-10 text-primary-600"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      </div>
    );
  }

  if (!proposal) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 mb-4">Proposal not found</p>
          <Link to={proposalsHref} className="text-primary-600 hover:text-primary-700">
            Back to proposals
          </Link>
        </div>
      </div>
    );
  }

  const isPending = proposal.status === GameProposalStatus.PENDING;
  const isCountered = proposal.status === GameProposalStatus.COUNTERED;
  const isOpponent = myTeams.some(t => t.id === proposal.opposingTeamId);
  const isProposer = myTeams.some(t => t.id === proposal.proposingTeamId);
  const isParticipant = isOpponent || isProposer;

  // A proposal is a back-and-forth negotiation: the team that made the latest
  // offer waits, and the OTHER team responds (accept / decline / counter).
  // `respondingTeamId` (shared with the proposals list) encodes whose turn it is.
  const counters = proposal.counterProposals;
  const lastCounter = counters.length > 0 ? counters[counters.length - 1] : undefined;
  const respondsNextId = respondingTeamId(proposal);
  const respondingTeamName =
    respondsNextId === proposal.proposingTeamId
      ? proposal.proposingTeamName ?? 'the proposing team'
      : proposal.opposingTeamName ?? 'the opponent';
  const isMyTurn = myTeams.some(t => t.id === respondsNextId);
  const canAct = (isPending || isCountered) && isMyTurn;
  const isWaiting = isParticipant && !isMyTurn && (isPending || isCountered);

  const statusColors: Record<string, string> = {
    [GameProposalStatus.PENDING]: 'bg-yellow-100 text-yellow-800',
    [GameProposalStatus.ACCEPTED]: 'bg-green-100 text-green-800',
    [GameProposalStatus.COUNTERED]: 'bg-blue-100 text-blue-800',
    [GameProposalStatus.EXPIRED]: 'bg-gray-100 text-gray-800',
    [GameProposalStatus.CANCELLED]: 'bg-red-100 text-red-800',
    [GameProposalStatus.DECLINED]: 'bg-red-100 text-red-800',
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <Link to={proposalsHref} className="text-sm text-primary-600 hover:text-primary-700">
            &larr; Back to proposals
          </Link>
        </div>

        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-5 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-900">Game Proposal</h2>
              <span
                className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                  statusColors[proposal.status] || 'bg-gray-100 text-gray-800'
                }`}
              >
                {proposal.status}
              </span>
            </div>
          </div>

          <div className="px-6 py-5 space-y-6">
            {(error || actionError) && (
              <div className="rounded-md bg-danger-50 p-4">
                <p className="text-sm text-danger-800">{actionError || error}</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              {proposal.divisionName && (
                <div className="col-span-2">
                  <span className="text-sm text-gray-500">Division</span>
                  <p className="text-sm font-medium text-gray-900">{proposal.divisionName}</p>
                </div>
              )}
              <div>
                <span className="text-sm text-gray-500">Proposing Team</span>
                <p className="text-sm font-medium text-gray-900">
                  {proposal.proposingTeamName ?? proposal.proposingTeamId}
                </p>
              </div>
              <div>
                <span className="text-sm text-gray-500">Opposing Team</span>
                <p className="text-sm font-medium text-gray-900">
                  {proposal.opposingTeamName ?? proposal.opposingTeamId}
                </p>
              </div>
              <div>
                <span className="text-sm text-gray-500">Created</span>
                <p className="text-sm font-medium text-gray-900">
                  {proposal.createdAt ? new Date(proposal.createdAt).toLocaleDateString() : 'N/A'}
                </p>
              </div>
              <div>
                <span className="text-sm text-gray-500">Expires</span>
                <p className="text-sm font-medium text-gray-900">
                  {new Date(proposal.expiresAt).toLocaleDateString()}
                </p>
              </div>
            </div>

            {/* Proposed Time Slots (the original offer). Once a counter exists, the
                original is superseded — accept moves to the latest counter below. */}
            <div>
              <h3 className="text-sm font-medium text-gray-900 mb-3">
                {lastCounter ? 'Original Proposed Time Slots' : 'Proposed Time Slots'}
              </h3>
              <div className="space-y-3">
                {proposal.proposedSlots.map((slot, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between border border-gray-200 rounded-md p-3"
                  >
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {slot.date} at {slot.time}
                      </p>
                      <p className="text-sm text-gray-500">
                        {slot.venue.name} - {slot.venue.address}
                      </p>
                    </div>
                    {canAct && !lastCounter && (
                      <button
                        type="button"
                        onClick={() => handleAcceptSlot(index)}
                        className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
                      >
                        Accept
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Accepted Slot */}
            {proposal.acceptedSlot && (
              <div className="bg-green-50 border border-green-200 rounded-md p-4">
                <h3 className="text-sm font-medium text-green-800 mb-2">Accepted Slot</h3>
                <p className="text-sm text-green-700">
                  {proposal.acceptedSlot.date} at {proposal.acceptedSlot.time} -{' '}
                  {proposal.acceptedSlot.venue.name}
                </p>
              </div>
            )}

            {/* Counter Proposals */}
            {proposal.counterProposals.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-gray-900 mb-3">Counter-Proposals</h3>
                <div className="space-y-4">
                  {proposal.counterProposals.map((counter, cIdx) => {
                    // Only the most recent counter is the live offer to act on.
                    const isLatest = cIdx === proposal.counterProposals.length - 1;
                    return (
                      <div key={cIdx} className="border border-blue-200 rounded-md p-4 bg-blue-50">
                        <p className="text-xs text-blue-600 mb-2">
                          From: {counter.teamName ?? counter.teamId} -{' '}
                          {new Date(counter.createdAt).toLocaleDateString()}
                          {isLatest && <span className="ml-1 font-medium">(latest offer)</span>}
                        </p>
                        {counter.slots.map((slot, sIdx) => (
                          <div key={sIdx} className="flex items-center justify-between py-1">
                            <p className="text-sm text-blue-800">
                              {slot.date} at {slot.time} - {slot.venue.name} - {slot.venue.address}
                            </p>
                            {canAct && isLatest && (
                              <button
                                type="button"
                                onClick={() => handleAcceptSlot(sIdx)}
                                className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
                              >
                                Accept
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* The viewer made the latest offer — they wait for the other team. */}
            {isWaiting && (
              <div className="rounded-md bg-primary-50 p-3 text-sm text-primary-800">
                Waiting for {respondingTeamName} to accept, decline, or counter the latest offer.
              </div>
            )}

            {/* The responding team's actions: counter or decline (accept is per-slot above). */}
            {canAct && !showCounterForm && (
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => setShowCounterForm(true)}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Counter-Propose
                </button>
                <button
                  type="button"
                  onClick={handleDecline}
                  className="inline-flex items-center px-4 py-2 border border-danger-300 rounded-md shadow-sm text-sm font-medium text-danger-700 bg-white hover:bg-danger-50"
                >
                  Decline
                </button>
              </div>
            )}

            {showCounterForm && (
              <form onSubmit={handleCounter} className="border border-gray-200 rounded-md p-4">
                <h3 className="text-sm font-medium text-gray-900 mb-4">Counter-Proposal</h3>
                <div className="space-y-3">
                  {counterSlots.map((slot, index) => (
                    <SlotEditor
                      key={index}
                      index={index}
                      slot={slot}
                      onChange={updated => updateCounterSlot(index, updated)}
                      onRemove={
                        counterSlots.length > 1 ? () => removeCounterSlot(index) : undefined
                      }
                      showErrors={counterAttempted}
                      minDate={seasonBounds.minDate}
                      maxDate={seasonBounds.maxDate}
                    />
                  ))}
                </div>
                <div className="mt-4 flex justify-between">
                  <button
                    type="button"
                    onClick={addCounterSlot}
                    className="text-sm text-primary-600 hover:text-primary-700"
                  >
                    + Add another slot
                  </button>
                  <div className="flex space-x-3">
                    <button
                      type="button"
                      onClick={() => setShowCounterForm(false)}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
                    >
                      Submit Counter-Proposal
                    </button>
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProposalDetailPage;
